import React, { useState, useEffect } from 'react';
import { DollarSign, Download } from 'lucide-react';
import { Table } from '@/components';
import { accountingApi } from '@/services/accountingApi';
import toast from 'react-hot-toast';

export const AccountingOrders: React.FC = () => {
    const [orders, setOrders] = useState<any[]>([]);
    const [totals, setTotals] = useState<any>({});
    const [loading, setLoading] = useState(true);
    const [startDate, setStartDate] = useState<string>(
        new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0]
    );
    const [endDate, setEndDate] = useState<string>(
        new Date().toISOString().split('T')[0]
    );
    const [offset, setOffset] = useState(0);
    const limit = 50;

    useEffect(() => {
        loadOrders();
    }, [startDate, endDate, offset]);

    const loadOrders = async () => {
        setLoading(true);
        try {
            const data = await accountingApi.getOrderFinancials({
                dateStart: startDate,
                dateEnd: endDate,
                limit,
                offset,
            });
            setOrders(data.orders || []);
            setTotals(data.totals || {});
        } catch (error) {
            console.error('Finansal veriler yüklenemedi:', error);
            toast.error('Finansal veriler yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    const tableData = orders.map((order) => {
        const rev = order.totalRevenue || 0;
        const cFee = order.courierFee || 0;
        const isDirect = order.isDirectSale === true || order.agreementType === 'direct_sale' || !order.vendorId;
        const pComm = order.platformShare != null ? order.platformShare : (order.vendorCommission || 0);
        const vShare = isDirect ? 0 : (order.vendorShare != null ? order.vendorShare : (rev - pComm));

        return {
            id: order.orderId,
            orderId: order.orderId,
            vendorId: order.vendorId,
            isDirect,
            saleType: isDirect ? 'Doğrudan Satış (Kargo)' : 'Vendor (Çiçekçi)',
            revenue: `₺${rev.toFixed(2)}`,
            platformCommission: isDirect ? `₺${rev.toFixed(2)} (%100)` : `₺${pComm.toFixed(2)}`,
            vendorPayout: isDirect ? '₺0.00' : `₺${vShare.toFixed(2)}`,
            courierFee: `₺${cFee.toFixed(2)}`,
            date: new Date(order.reportedDate).toLocaleDateString('tr-TR'),
        };
    });

    const handleExportCSV = () => {
        const headers = [
            'Sipariş ID',
            'Model / Satış Tipi',
            'Ciro (Satış)',
            'Platform Payı',
            'Satıcı Hakedişi',
            'Kurye / Kargo Ücreti',
            'Tarih',
        ];
        const rows = tableData.map((row) => [
            row.orderId,
            row.saleType,
            row.revenue,
            row.platformCommission,
            row.vendorPayout,
            row.courierFee,
            row.date,
        ]);

        const csvContent = [
            headers.join(','),
            ...rows.map((row) => row.join(',')),
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `finansal_rapor_${new Date().getTime()}.csv`;
        link.click();
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100">
                        Sipariş Finansalları
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400 mt-1">
                        Platform komisyonu, doğrudan satış ve satıcı hakediş analiz dökümü
                    </p>
                </div>
                <button
                    onClick={handleExportCSV}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                >
                    <Download size={20} />
                    CSV İndir
                </button>
            </div>

            {/* Filters */}
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                <div className="flex gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Başlangıç Tarihi
                        </label>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Bitiş Tarihi
                        </label>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg shadow">
                    <p className="text-sm text-blue-600 dark:text-blue-400">Toplam Satış (Ciro)</p>
                    <p className="text-2xl font-bold text-blue-700 dark:text-blue-300 mt-1">
                        ₺
                        {(totals.totalRevenue || 0).toLocaleString('tr-TR', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                        })}
                    </p>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg shadow">
                    <p className="text-sm text-green-600 dark:text-green-400 font-semibold">
                        Platform Toplam Payı (Gelir)
                    </p>
                    <p className="text-2xl font-bold text-green-700 dark:text-green-300 mt-1">
                        ₺
                        {(totals.platformCommission != null ? totals.platformCommission : (totals.totalVendorCommissions || 0)).toLocaleString('tr-TR', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                        })}
                    </p>
                </div>
                <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg shadow">
                    <p className="text-sm text-purple-600 dark:text-purple-400 font-semibold">
                        Vendor Hakedişleri (Ödenecek)
                    </p>
                    <p className="text-2xl font-bold text-purple-700 dark:text-purple-300 mt-1">
                        ₺
                        {(totals.vendorPayouts != null ? totals.vendorPayouts : ((totals.totalRevenue || 0) - (totals.totalVendorCommissions || 0))).toLocaleString('tr-TR', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                        })}
                    </p>
                </div>
                <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg shadow">
                    <p className="text-sm text-red-600 dark:text-red-400">Kurye / Kargo Gideri</p>
                    <p className="text-2xl font-bold text-red-700 dark:text-red-300 mt-1">
                        ₺
                        {(totals.totalCourierFees || 0).toLocaleString('tr-TR', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                        })}
                    </p>
                </div>
            </div>

            {/* Table */}
            <Table
                headers={['Sipariş ID', 'Model / Satış Tipi', 'Ciro (Satış)', 'Platform Payı', 'Satıcı Hakedişi', 'Kurye / Kargo', 'Tarih']}
                data={tableData}
                renderCell={(item: any, header: string) => {
                    switch (header) {
                        case 'Sipariş ID':
                            return item.orderId;
                        case 'Model / Satış Tipi':
                            return item.isDirect ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
                                    📦 Doğrudan Satış (Kargo)
                                </span>
                            ) : (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">
                                    🌸 Vendor (Çiçekçi)
                                </span>
                            );
                        case 'Ciro (Satış)':
                            return item.revenue;
                        case 'Platform Payı':
                            return item.platformCommission;
                        case 'Satıcı Hakedişi':
                            return item.vendorPayout;
                        case 'Kurye / Kargo':
                            return item.courierFee;
                        case 'Tarih':
                            return item.date;
                        default:
                            return '-';
                    }
                }}
            />
        </div>
    );
};

export default AccountingOrders;
